import React, { useState } from 'react';
import Modal from './Modal';
import { blogsApi, usersApi } from '../api/client';

interface AddBlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBlogAdded: () => void;
}

const AddBlogModal: React.FC<AddBlogModalProps> = ({ isOpen, onClose, onBlogAdded }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'active' as 'active' | 'inactive' | 'archived',
    theme: 'default',
    isPublic: true,
    allowComments: true,
    moderateComments: false,
    showAuthor: true,
    showDate: true,
    postsPerPage: 10,
    category: 'personal',
    tags: '',
    language: 'en'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'active',
      theme: 'default',
      isPublic: true,
      allowComments: true,
      moderateComments: false,
      showAuthor: true,
      showDate: true,
      postsPerPage: 10,
      category: 'personal',
      tags: '',
      language: 'en'
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Blog title is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Blog description is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current user for userId
      const currentUser = await usersApi.getCurrentUser();
      
      await blogsApi.createBlog({
        userId: currentUser.data.id,
        title: formData.title,
        description: formData.description,
        status: formData.status,
        theme: formData.theme,
        settings: {
          isPublic: formData.isPublic,
          allowComments: formData.allowComments,
          moderateComments: formData.moderateComments,
          showAuthor: formData.showAuthor,
          showDate: formData.showDate,
          postsPerPage: formData.postsPerPage
        },
        metadata: {
          category: formData.category,
          tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
          language: formData.language
        }
      });

      setSuccess(true);
      setTimeout(() => {
        onBlogAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error creating blog:', err);
      setError('Failed to create blog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Blog">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Blog created successfully!
          </div>
        )}

        <div className="modal-form-group">
          <label>Blog Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="My Awesome Blog"
            disabled={loading}
          />
        </div>

        <div className="modal-form-group">
          <label>Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="A brief description of what your blog is about..."
            disabled={loading}
            rows={3}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              disabled={loading}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              disabled={loading}
            >
              <option value="personal">Personal</option>
              <option value="technology">Technology</option>
              <option value="lifestyle">Lifestyle</option>
              <option value="business">Business</option>
              <option value="travel">Travel</option>
              <option value="food">Food</option>
              <option value="health">Health</option>
              <option value="education">Education</option>
              <option value="entertainment">Entertainment</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Theme</label>
            <select
              value={formData.theme}
              onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
              disabled={loading}
            >
              <option value="default">Default</option>
              <option value="minimal">Minimal</option>
              <option value="modern">Modern</option>
              <option value="classic">Classic</option>
              <option value="dark">Dark</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Posts Per Page</label>
            <input
              type="number"
              min="1"
              max="50"
              value={formData.postsPerPage}
              onChange={(e) => setFormData({ ...formData, postsPerPage: parseInt(e.target.value) || 10 })}
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-group">
          <label>Tags</label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            placeholder="technology, personal, thoughts (comma separated)"
            disabled={loading}
          />
        </div>

        {/* Settings */}
        <h4 style={{ margin: '1rem 0 0.5rem 0', color: '#666' }}>Blog Settings</h4>
        
        <div className="modal-form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.isPublic}
              onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
              disabled={loading}
            />
            <span style={{ marginLeft: '8px' }}>Public blog (visible to everyone)</span>
          </label>
        </div>

        <div className="modal-form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.allowComments}
              onChange={(e) => setFormData({ ...formData, allowComments: e.target.checked })}
              disabled={loading}
            />
            <span style={{ marginLeft: '8px' }}>Allow comments on posts</span>
          </label>
        </div>

        <div className="modal-form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.moderateComments}
              onChange={(e) => setFormData({ ...formData, moderateComments: e.target.checked })}
              disabled={loading}
            />
            <span style={{ marginLeft: '8px' }}>Moderate comments before publishing</span>
          </label>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.showAuthor}
                onChange={(e) => setFormData({ ...formData, showAuthor: e.target.checked })}
                disabled={loading}
              />
              <span style={{ marginLeft: '8px' }}>Show author</span>
            </label>
          </div>

          <div className="modal-form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.showDate}
                onChange={(e) => setFormData({ ...formData, showDate: e.target.checked })}
                disabled={loading}
              />
              <span style={{ marginLeft: '8px' }}>Show date</span>
            </label>
          </div>
        </div>

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Blog'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddBlogModal;